import React, { useState } from 'react';
import theme from './ChangeRequestParticipantSelectList.module.scss';
import {
    Banner,
    Button,
    ButtonBar,
    LoadingIndicator,
    Modal,
    ModalBody,
    ModalHeader,
    Toast,
} from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { changeParticipantUser, getChangeRequestAvailableParticipants } from '@/services/change-requests';
import { useAsyncStateWithIsLoading, useCategories, useGetProjectId } from '@/utils/hooks';
import { SelectionList } from '@ygtechnologies/rubus-ui';
import UserIcon from '@/components/Icons/EntityIcon/UserIcon';
import { ChangeRequestParticipantInfo, UpdateFields } from '@/types';
import { findTopLevelCategory } from '@/utils/categories';
import { intersection, uniq } from 'lodash-es';

interface ChangeRequestParticipantChangeModalProps {
    isOpen: boolean;
    toggle(): void;
    oldParticipant: ChangeRequestParticipantInfo;
    onSubmit(changes: UpdateFields<ChangeRequestParticipantInfo>): void;
}

const ChangeRequestParticipantChangeModal = ({
    isOpen,
    toggle,
    oldParticipant,
    onSubmit,
}: ChangeRequestParticipantChangeModalProps) => {
    const projectId = useGetProjectId();
    const [isSaving, setIsSaving] = useState(false);
    const [selectedParticipantId, setSelectedParticipant] = useState<number | null>(null);
    const [availableParticipants, , , isLoading] = useAsyncStateWithIsLoading(
        () => getChangeRequestAvailableParticipants(projectId!),
        [projectId],
        []
    );

    // doing this incase there's multiple users with same userId in different companies?
    // Id is 0 from the API
    const fixedParticipants = availableParticipants.map((x, i) => ({ ...x, id: i }));
    const categories = useCategories();

    const activeCategories = uniq(
        oldParticipant.offerLines
            ?.filter((x) => x.itemCategoryId)
            .map((x) => findTopLevelCategory(categories, x.itemCategoryId!)?.id)
    );

    const filteredParticipants = fixedParticipants.map((x) => ({
        ...x,
        isDisabled:
            x.isAdministrator || intersection(x.availableCategories, activeCategories).length === activeCategories.length,
    }));

    const handleSubmit = async () => {
        setIsSaving(true);
        const newParticipant = fixedParticipants!.find((x) => x.id === selectedParticipantId)!;
        await changeParticipantUser(oldParticipant.id, newParticipant.profileId, newParticipant.userId);
        setIsSaving(false);
        onSubmit({
            userId: newParticipant.userId,
            profileId: newParticipant.profileId,
            displayName: newParticipant.displayName,
        });
        Toast.success(<Trans>Participant user changed.</Trans>);
    };

    return (
        <Modal toggle={toggle} isOpen={isOpen}>
            <ModalHeader>
                <Trans>Choose a new user</Trans>
            </ModalHeader>
            <ModalBody>
                {isLoading === null ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <SelectionList selectionMode="single">
                            {filteredParticipants
                                .filter((x) => x.userId !== oldParticipant.userId)
                                .map((ap) => (
                                    <SelectionList.Item
                                        disabled={ap.isDisabled}
                                        key={ap.userId}
                                        selected={selectedParticipantId === ap.id}
                                        onChange={() => setSelectedParticipant(ap.id)}
                                        label={
                                            <div className={theme.itemContent}>
                                                <UserIcon userId={ap.userId} sizeInPx={36} />
                                                <span className={theme.name}>{ap.displayName}</span>
                                                <small>{ap.profileName}</small>
                                            </div>
                                        }
                                    />
                                ))}
                        </SelectionList>
                        <Banner type="info">
                            <Trans>
                                <strong>Please note</strong> you can only select a user who has the same categories as those
                                in the offer, or an administrator.
                            </Trans>
                        </Banner>
                        <ButtonBar>
                            <Button onClick={toggle} color="tertiary">
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button
                                loading={isSaving}
                                disabled={selectedParticipantId === null}
                                onClick={handleSubmit}
                                color="primary">
                                <Trans>Submit</Trans>
                            </Button>
                        </ButtonBar>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default ChangeRequestParticipantChangeModal;
